const DESTINATIONS = new Set([
  "fonttbl", "colortbl", "stylesheet", "info", "pict", "object", "header", "headerl",
  "headerr", "headerf", "footer", "footerl", "footerr", "footerf", "filetbl", "listtable",
  "listoverridetable", "revtbl", "rsidtbl", "generator", "xmlnstbl", "datastore", "themedata"
]);

const SYMBOLS = Object.freeze({
  emdash: "—", endash: "–", bullet: "•", lquote: "‘", rquote: "’",
  ldblquote: "“", rdblquote: "”", tab: "    ", line: "\n"
});

function styleOf(state) {
  return {
    bold: state.bold || undefined,
    italic: state.italic || undefined,
    underline: state.underline || undefined,
    strike: state.strike || undefined
  };
}

function sameStyle(left, right) {
  return Boolean(left.bold) === Boolean(right.bold)
    && Boolean(left.italic) === Boolean(right.italic)
    && Boolean(left.underline) === Boolean(right.underline)
    && Boolean(left.strike) === Boolean(right.strike);
}

export function decodeRtfBytes(bytes) {
  return new TextDecoder("windows-1252").decode(bytes);
}

export function parseRtfDocument(source) {
  const rtf = String(source ?? "");
  if (!/^\s*\{\\rtf(?:1)?\b/i.test(rtf))
    throw new Error("The source is not a valid RTF document.");

  const initial = {
    bold: false, italic: false, underline: false, strike: false,
    skip: false, ignorable: false, ucSkip: 1
  };
  const stack = [initial];
  const blocks = [];
  let runs = [];
  let skippedDestination = false;
  let fallbackToSkip = 0;

  const current = () => stack[stack.length - 1];
  const append = text => {
    if (!text || current().skip) return;
    const style = styleOf(current());
    const previous = runs[runs.length - 1];
    if (previous && sameStyle(previous, style)) previous.text += text;
    else runs.push({ text, ...style });
  };
  const paragraph = () => {
    while (runs.length && !runs[runs.length - 1].text) runs.pop();
    if (runs.some(run => run.text)) blocks.push({ type: "paragraph", runs });
    runs = [];
  };

  for (let index = 0; index < rtf.length;) {
    const character = rtf[index];

    if (fallbackToSkip > 0 && character !== "{" && character !== "}") {
      if (character === "\\" && rtf[index + 1] === "'" && /^[0-9a-f]{2}$/i.test(rtf.slice(index + 2, index + 4)))
        index += 4;
      else
        index += character === "\\" && ["\\", "{", "}"].includes(rtf[index + 1]) ? 2 : 1;
      fallbackToSkip -= 1;
      continue;
    }

    if (character === "{") {
      stack.push({ ...current(), ignorable: false });
      index += 1;
      continue;
    }
    if (character === "}") {
      if (stack.length > 1) stack.pop();
      index += 1;
      continue;
    }
    if (character !== "\\") {
      append(character === "\r" || character === "\n" ? "" : character);
      index += 1;
      continue;
    }

    const escaped = rtf[index + 1];
    if (["\\", "{", "}"].includes(escaped)) {
      append(escaped);
      index += 2;
      continue;
    }
    if (escaped === "~") {
      append("\u00a0");
      index += 2;
      continue;
    }
    if (escaped === "_") {
      append("‑");
      index += 2;
      continue;
    }
    if (escaped === "*") {
      current().ignorable = true;
      index += 2;
      continue;
    }
    if (escaped === "'" && /^[0-9a-f]{2}$/i.test(rtf.slice(index + 2, index + 4))) {
      const byte = Number.parseInt(rtf.slice(index + 2, index + 4), 16);
      append(new TextDecoder("windows-1252").decode(Uint8Array.of(byte)));
      index += 4;
      continue;
    }

    const match = /^\\([a-z]+)(-?\d+)? ?/i.exec(rtf.slice(index));
    if (!match) {
      index += Math.min(2, rtf.length - index);
      continue;
    }
    const word = match[1].toLowerCase();
    const value = match[2] == null ? null : Number(match[2]);
    index += match[0].length;

    if (current().ignorable || DESTINATIONS.has(word)) {
      current().skip = true;
      skippedDestination = true;
      continue;
    }
    if (current().skip) continue;

    if (word === "b") current().bold = value !== 0;
    else if (word === "i") current().italic = value !== 0;
    else if (word === "ul") current().underline = value !== 0;
    else if (word === "ulnone") current().underline = false;
    else if (word === "strike") current().strike = value !== 0;
    else if (word === "plain") Object.assign(current(), { bold: false, italic: false, underline: false, strike: false });
    else if (word === "par") paragraph();
    else if (word === "uc" && Number.isInteger(value)) current().ucSkip = Math.max(0, Math.min(10, value));
    else if (word === "u" && Number.isInteger(value)) {
      const point = value < 0 ? value + 65536 : value;
      append(String.fromCodePoint(point));
      fallbackToSkip = current().ucSkip;
    } else if (SYMBOLS[word]) append(SYMBOLS[word]);
  }
  paragraph();

  const textLength = blocks.reduce((sum, block) =>
    sum + block.runs.reduce((runSum, run) => runSum + run.text.length, 0), 0);
  return {
    blocks,
    textLength,
    diagnostics: skippedDestination
      ? ["Embedded objects, images, headers, footers, and document metadata are omitted."]
      : []
  };
}
